import React, { useState, useEffect, useRef } from 'react';
import { Download, RefreshCw, Image as ImageIcon, Loader2, Share2, MessageCircle, Send, Facebook, Link2, Check } from 'lucide-react';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { QR_APP_DATA } from '../../utils/qrData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  corbalar: 'Corbalar',
  ana_yemekler: 'Ana Yemekler',
  yardimci_yemekler: 'Yan Lezzetler',
  salatalar: 'Salatalar',
  mezeler: 'Mezeler'
};

const CATEGORY_COLORS = {
  corbalar: '#C2703E',
  ana_yemekler: '#8B5E3C',
  yardimci_yemekler: '#6B8E5A',
  salatalar: '#A67B5B',
  mezeler: '#8B6E54'
};

// Multi-language taglines for flyer (kept short and visually balanced)
const FLYER_TRANSLATIONS = [
  { flag: 'TR', tagline: 'SADECE €10 · 4 KATEGORİ · MEZE HARİÇ',          appCta: 'Tüm menü için uygulamamızı ziyaret edin →' },
  { flag: 'EN', tagline: 'ONLY €10 · 4 CATEGORIES · NO APPETIZERS',        appCta: 'Visit our app for the full menu →' },
  { flag: 'RU', tagline: 'ВСЕГО €10 · 4 КАТЕГОРИИ · БЕЗ ЗАКУСОК',          appCta: 'Полное меню в приложении →' },
  { flag: 'SR', tagline: 'САМО €10 · 4 КАТЕГОРИЈЕ · БЕЗ ПРЕДЈЕЛА',         appCta: 'Цео мени у апликацији →' },
  { flag: 'ME', tagline: 'SAMO €10 · 4 KATEGORIJE · BEZ MEZEA',            appCta: 'Cijeli meni u aplikaciji →' },
];

const FALLBACK_IMAGES = {
  corbalar: [
    'https://images.unsplash.com/photo-1636044984153-dd292d223bdb?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1724856774356-99331a86866f?w=400&h=400&fit=crop&crop=center'
  ],
  ana_yemekler: [
    'https://images.unsplash.com/photo-1663861623497-2151b2bb21fe?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1765894711192-d35787eee3b6?w=400&h=400&fit=crop&crop=center'
  ],
  yardimci_yemekler: [
    'https://images.unsplash.com/photo-1733414717515-d997dafb7341?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1644131447497-8723db691320?w=400&h=400&fit=crop&crop=center'
  ],
  salatalar: [
    'https://images.unsplash.com/photo-1751890893842-7807b463b344?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1644504439611-ddc302de87ea?w=400&h=400&fit=crop&crop=center'
  ],
  mezeler: [
    'https://images.unsplash.com/photo-1751890893842-7807b463b344?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1644504439611-ddc302de87ea?w=400&h=400&fit=crop&crop=center'
  ]
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const getItemImage = (item, catKey, idx) => {
  if (item.image_url) {
    if (item.image_url.startsWith('http')) return item.image_url;
    return `${BASE_URL}${item.image_url}`;
  }
  const fallbacks = FALLBACK_IMAGES[catKey] || FALLBACK_IMAGES.ana_yemekler;
  return fallbacks[idx % fallbacks.length];
};

const FlyerGenerator = ({ token }) => {
  const [cateringMenus, setCateringMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const flyerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [menuRes, todayRes] = await Promise.all([
          axios.get(`${API}/admin/catering`, { headers }),
          axios.get(`${API}/catering/today`).catch(() => ({ data: null }))
        ]);
        const menus = menuRes.data || [];
        // Merge a la carte injected items into admin menus
        const todayMenu = todayRes.data;
        if (todayMenu && todayMenu.date) {
          const existingIdx = menus.findIndex(m => m.date === todayMenu.date);
          if (existingIdx >= 0) {
            // Merge injected items into existing menu
            for (const cat of ['corbalar', 'ana_yemekler', 'yardimci_yemekler', 'salatalar', 'mezeler']) {
              const todayItems = (todayMenu[cat] || []).filter(i => i.from_alakart);
              if (todayItems.length > 0) {
                if (!menus[existingIdx][cat]) menus[existingIdx][cat] = [];
                menus[existingIdx][cat] = [...menus[existingIdx][cat], ...todayItems];
              }
            }
          } else {
            // Today has no admin menu but has injected items
            const hasItems = ['corbalar', 'ana_yemekler', 'yardimci_yemekler', 'salatalar', 'mezeler'].some(c => (todayMenu[c] || []).length > 0);
            if (hasItems) {
              menus.unshift({ ...todayMenu, id: 'today', is_active: true });
            }
          }
        }
        setCateringMenus(menus);
        const active = menus.find(m => m.is_active) || menus[0];
        if (active) setSelectedMenu(active);
        // Try to load logo
        try {
          const settingsRes = await axios.get(`${API}/admin/settings`, { headers });
          if (settingsRes.data?.logo_url) setLogoUrl(settingsRes.data.logo_url);
        } catch {}
      } catch { toast.error('Menu yuklenemedi'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Safe data extraction from potentially tainted canvas
  const safeToDataURL = (canvas) => {
    try {
      return canvas.toDataURL('image/png');
    } catch {
      // Tainted canvas fallback
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const c2 = document.createElement('canvas');
      c2.width = canvas.width;
      c2.height = canvas.height;
      c2.getContext('2d').putImageData(imageData, 0, 0);
      return c2.toDataURL('image/png');
    }
  };

  const handleDownload = async () => {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(flyerRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#F5EDE4'
      });
      const link = document.createElement('a');
      const dateStr = selectedMenu?.date || 'menu';
      link.download = `gunluk-menu-${dateStr}.png`;
      link.href = safeToDataURL(canvas);
      link.click();
      toast.success('Flyer indirildi!');
    } catch (err) {
      console.error('Flyer download error:', err);
      toast.error('Flyer indirilemedi: ' + (err.message || ''));
    } finally { setDownloading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getFlyerBlob = async () => {
    if (!flyerRef.current) return null;
    const canvas = await html2canvas(flyerRef.current, {
      scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#F5EDE4'
    });
    const dataUrl = safeToDataURL(canvas);
    const res = await fetch(dataUrl);
    return res.blob();
  };

  const handleNativeShare = async () => {
    if (!navigator.share) { toast.error('Tarayiciniz paylasimi desteklemiyor'); return; }
    setSharing(true);
    try {
      const blob = await getFlyerBlob();
      const file = new File([blob], `gunluk-menu-${selectedMenu?.date || 'menu'}.png`, { type: 'image/png' });
      await navigator.share({ title: 'Gunun Menusu - Central Restaurant', text: shareText, files: [file] });
      toast.success('Paylasim basarili!');
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Paylasim yapilamadi');
    } finally { setSharing(false); }
  };

  const appUrl = 'https://centralhotel.org';
  const shareText = `Central Restaurant - Gunun Menusu (${formatDate(selectedMenu?.date)})\nSadece €10.00 — Çorba + Ana Yemek + Yardımcı Yemek + Salata (Meze hariç)\nOnline siparis: ${appUrl}`;

  const handleWhatsAppShare = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  const handleWhatsAppQuickSend = async () => {
    setSharing(true);
    try {
      // On mobile with Web Share API - share image directly to WhatsApp
      if (navigator.share && navigator.canShare) {
        const blob = await getFlyerBlob();
        if (blob) {
          const file = new File([blob], `gunluk-menu-${selectedMenu?.date || 'menu'}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title: 'Gunun Menusu - Central Restaurant', text: shareText, files: [file] });
            toast.success('Paylasim basarili!');
            setSharing(false);
            return;
          }
        }
      }
      // Desktop fallback: download image + open WhatsApp
      const canvas = await html2canvas(flyerRef.current, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#F5EDE4' });
      const link = document.createElement('a');
      link.download = `gunluk-menu-${selectedMenu?.date || 'menu'}.png`;
      link.href = safeToDataURL(canvas);
      link.click();
      setTimeout(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
      }, 500);
      toast.success('Flyer indirildi! WhatsApp aciliyor - gorseli eklemeyi unutmayin.', { duration: 6000 });
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Islem yapilamadi');
    } finally { setSharing(false); }
  };

  const handleTelegramShare = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(appUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
  };

  const handleFacebookShare = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareText).then(() => {
      setLinkCopied(true);
      toast.success('Metin kopyalandi!');
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => toast.error('Kopyalanamadi'));
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-600 border-t-transparent mx-auto mb-3" />
        <p className="text-charcoal/50">Menu yukleniyor...</p>
      </div>
    );
  }

  if (!selectedMenu) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-stone-200">
        <ImageIcon className="w-14 h-14 text-charcoal/20 mx-auto mb-3" />
        <p className="text-charcoal/50 text-lg mb-1">Aktif catering menusu bulunamadi</p>
        <p className="text-charcoal/40 text-sm">Oncelikle Catering sekmesinden bir menu olusturun</p>
      </div>
    );
  }

  const categories = ['corbalar', 'ana_yemekler', 'yardimci_yemekler', 'salatalar', 'mezeler'];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-600/10 rounded-full flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h2 className="font-playfair text-xl font-semibold text-charcoal" data-testid="flyer-title">Gunluk Menu Flyer</h2>
            <p className="text-sm text-charcoal/60">Indirilir flyer olustur ve paylas</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {cateringMenus.length > 1 && (
            <select value={selectedMenu?.id || ''} onChange={e => setSelectedMenu(cateringMenus.find(m => m.id === e.target.value))}
              className="text-sm border rounded-xl px-3 py-2 bg-white" data-testid="flyer-menu-select">
              {cateringMenus.map(m => (
                <option key={m.id} value={m.id}>{m.date} {m.is_active ? '(Aktif)' : ''}</option>
              ))}
            </select>
          )}
          <Button onClick={handleDownload} disabled={downloading} className="bg-amber-700 text-white hover:bg-amber-600 rounded-full" data-testid="flyer-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {downloading ? 'Hazirlaniyor...' : 'PNG Indir'}
          </Button>
        </div>
      </div>

      {/* Flyer Preview */}
      <div className="flex justify-center">
        <div className="shadow-2xl rounded-2xl overflow-hidden border border-stone-300" style={{ width: 800 }}>
          <div ref={flyerRef} style={{
            width: 800, position: 'relative', overflow: 'hidden',
            background: '#F5EDE4',
            fontFamily: "'Georgia', 'Times New Roman', serif"
          }} data-testid="flyer-canvas">

            {/* === CHARCOAL TOP HEADER === */}
            <div style={{
              background: 'linear-gradient(180deg, #1F1B17 0%, #2C2520 100%)',
              padding: '36px 40px 28px', position: 'relative'
            }}>
              {/* gold accent line top */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)'
              }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                {logoUrl && (
                  <img src={logoUrl} alt="Logo" crossOrigin="anonymous" style={{
                    width: 76, height: 76, borderRadius: '50%', objectFit: 'cover',
                    border: '2px solid rgba(212,175,55,0.5)', flexShrink: 0
                  }} />
                )}
                <div style={{ flex: 1 }}>
                  <h1 style={{
                    fontSize: 38, fontWeight: 700, color: '#F5EDE4',
                    letterSpacing: 4, margin: 0, lineHeight: 1
                  }}>CENTRAL</h1>
                  <p style={{
                    fontSize: 12, color: '#D4AF37', letterSpacing: 6,
                    marginTop: 6, textTransform: 'uppercase', fontWeight: 500,
                    fontStyle: 'italic'
                  }}>Hotel &amp; Restaurant</p>
                </div>
                <div style={{
                  background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)',
                  borderRadius: 6, padding: '8px 14px', textAlign: 'center'
                }}>
                  <p style={{
                    fontSize: 9, color: '#D4AF37', letterSpacing: 2, margin: 0,
                    textTransform: 'uppercase', fontWeight: 600
                  }}>Günün Menüsü</p>
                  <p style={{
                    fontSize: 13, color: '#F5EDE4', margin: '4px 0 0',
                    fontStyle: 'italic', fontWeight: 500
                  }}>{formatDate(selectedMenu.date)}</p>
                </div>
              </div>
            </div>

            {/* === HERO PRICE STRIP === */}
            <div style={{
              padding: '26px 40px 18px', display: 'flex', alignItems: 'center',
              gap: 24, borderBottom: '1px dashed rgba(60,36,21,0.2)',
              position: 'relative'
            }}>
              {/* huge €10 */}
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{
                  fontSize: 22, fontWeight: 700, color: '#C2703E',
                  marginRight: 2, transform: 'translateY(-18px)'
                }}>€</span>
                <span style={{
                  fontSize: 96, fontWeight: 900, color: '#3C2415',
                  lineHeight: 0.85, letterSpacing: -2, fontStyle: 'italic'
                }}>10</span>
                <span style={{
                  fontSize: 22, fontWeight: 700, color: '#8B7355',
                  marginLeft: 2, transform: 'translateY(-18px)'
                }}>.00</span>
              </div>
              {/* description */}
              <div style={{ flex: 1, paddingLeft: 18, borderLeft: '3px solid #C2703E' }}>
                <p style={{
                  fontSize: 16, fontWeight: 700, color: '#3C2415', margin: 0,
                  letterSpacing: 0.5, lineHeight: 1.25
                }}>4 Ana Kategoriden 1&apos;er Seçim</p>
                <p style={{
                  fontSize: 12, color: '#8B7355', margin: '6px 0 0',
                  letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600
                }}>Çorba · Ana Yemek · Yardımcı · Salata</p>
                <p style={{
                  fontSize: 11, color: '#C2703E', margin: '6px 0 0',
                  fontStyle: 'italic', fontWeight: 600
                }}>* Mezeler dahil değildir</p>
              </div>
            </div>

            {/* === MULTI-LANG TAGLINES === */}
            <div style={{
              padding: '12px 40px 14px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, transparent 100%)',
              borderBottom: '1px dashed rgba(60,36,21,0.12)'
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 22, rowGap: 5
              }}>
                {FLYER_TRANSLATIONS.map(({ flag, tagline }) => (
                  <div key={flag} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: '#FFFFFF',
                      background: '#3C2415', padding: '2px 6px', borderRadius: 3,
                      letterSpacing: 0.5, flexShrink: 0, minWidth: 22, textAlign: 'center'
                    }}>{flag}</span>
                    <span style={{
                      fontSize: 10.5, fontWeight: 600, color: '#3C2415',
                      letterSpacing: 0.3, lineHeight: 1.3
                    }}>{tagline}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* === MENU GRID === */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
              padding: '20px 40px 12px'
            }}>
              {categories.map(catKey => {
                const items = selectedMenu[catKey] || [];
                if (items.length === 0) return null;
                const color = CATEGORY_COLORS[catKey];
                return (
                  <div key={catKey} style={{
                    background: '#FFFFFF', borderRadius: 14, padding: 14,
                    boxShadow: '0 2px 8px rgba(60,36,21,0.06)',
                    borderLeft: `4px solid ${color}`
                  }}>
                    {/* Category Title */}
                    <div style={{ marginBottom: 10 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: color,
                        textTransform: 'uppercase', letterSpacing: 2.5
                      }}>{CATEGORY_LABELS[catKey]}</span>
                    </div>
                    {/* Items */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {items.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 56, height: 56, borderRadius: 10, overflow: 'hidden',
                            flexShrink: 0, background: '#E8DDD0',
                            border: `1px solid ${color}33`
                          }}>
                            <img src={getItemImage(item, catKey, idx)} alt={item.name} crossOrigin="anonymous" style={{
                              width: '100%', height: '100%', objectFit: 'cover'
                            }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              fontSize: 13, fontWeight: 600, color: '#3C2415',
                              margin: 0, lineHeight: 1.25
                            }}>{item.name}</p>
                            {item.description && (
                              <p style={{
                                fontSize: 10, color: '#8B7355', margin: '2px 0 0',
                                lineHeight: 1.3, overflow: 'hidden',
                                display: '-webkit-box', WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>{item.description}</p>
                            )}
                            {item.price_addition > 0 && (
                              <p style={{
                                fontSize: 11, fontWeight: 700, color: color,
                                margin: '3px 0 0'
                              }}>+€{item.price_addition.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* === APP CTA STRIP === */}
            <div style={{
              margin: '8px 40px 16px', borderRadius: 14, overflow: 'hidden',
              background: 'linear-gradient(135deg, #C2703E 0%, #A05729 100%)',
              padding: '16px 22px', position: 'relative',
              boxShadow: '0 4px 14px rgba(194,112,62,0.25)'
            }}>
              {/* Decorative circle */}
              <div style={{
                position: 'absolute', right: -30, top: -30, width: 140, height: 140,
                borderRadius: '50%', background: 'rgba(255,255,255,0.08)'
              }} />
              <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  fontSize: 28, lineHeight: 1, flexShrink: 0, marginTop: 2
                }}>📱</div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    fontSize: 14, fontWeight: 800, color: '#FFFFFF', margin: 0,
                    letterSpacing: 0.5, lineHeight: 1.2
                  }}>Bunlar yalnızca bugünün menüsü</p>
                  <p style={{
                    fontSize: 11.5, color: 'rgba(255,255,255,0.92)', margin: '4px 0 8px',
                    lineHeight: 1.4
                  }}>
                    Tüm <strong>A La Carte</strong>, içecekler ve özel siparişler için
                    <strong> uygulamamızı ziyaret edin</strong> →
                  </p>
                  {/* Multilingual CTA lines */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14, rowGap: 3,
                    paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.18)'
                  }}>
                    {FLYER_TRANSLATIONS.filter(t => t.flag !== 'TR').map(({ flag, appCta }) => (
                      <div key={flag} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          fontSize: 8, fontWeight: 800, color: '#C2703E',
                          background: 'rgba(255,255,255,0.95)', padding: '1px 4px', borderRadius: 2,
                          letterSpacing: 0.4, flexShrink: 0, minWidth: 18, textAlign: 'center'
                        }}>{flag}</span>
                        <span style={{
                          fontSize: 9.5, color: 'rgba(255,255,255,0.95)', fontWeight: 500,
                          lineHeight: 1.3, letterSpacing: 0.2
                        }}>{appCta}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* === CHARCOAL FOOTER WITH QR === */}
            <div style={{
              background: 'linear-gradient(0deg, #1F1B17 0%, #2C2520 100%)',
              padding: '20px 40px 22px', display: 'flex', alignItems: 'center',
              gap: 22, position: 'relative'
            }}>
              <div style={{
                background: '#F5EDE4', borderRadius: 12, padding: 8,
                flexShrink: 0
              }}>
                <img src={QR_APP_DATA} alt="QR Code" style={{
                  width: 96, height: 96, display: 'block'
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: 11, color: '#D4AF37', letterSpacing: 3, margin: 0,
                  textTransform: 'uppercase', fontWeight: 600
                }}>Hızlı Erişim</p>
                <p style={{
                  fontSize: 22, fontWeight: 700, color: '#F5EDE4', margin: '4px 0 0',
                  letterSpacing: 0.5
                }}>Online Sipariş</p>
                <p style={{
                  fontSize: 12, color: 'rgba(245,237,228,0.7)', margin: '6px 0 0',
                  lineHeight: 1.4
                }}>QR kodu telefonunuzla okutun, menüye saniyeler içinde ulaşın.</p>
                <div style={{
                  display: 'flex', gap: 14, marginTop: 10, fontSize: 11,
                  color: 'rgba(245,237,228,0.85)'
                }}>
                  <span>☎ +382 69 144 043</span>
                  <span style={{ color: 'rgba(212,175,55,0.5)' }}>·</span>
                  <span>📍 Budva, Montenegro</span>
                </div>
              </div>
            </div>

            {/* gold bottom line */}
            <div style={{
              width: '100%', height: 4,
              background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)'
            }} />

          </div>
        </div>
      </div>

      {/* Quick WhatsApp Send */}
      <div className="bg-gradient-to-r from-green-50 to-green-100/50 rounded-2xl border-2 border-green-200 p-5" data-testid="flyer-whatsapp-quick-send">
        <Button onClick={handleWhatsAppQuickSend} disabled={sharing}
          className="w-full bg-green-600 text-white hover:bg-green-700 py-6 text-lg font-bold rounded-xl shadow-lg shadow-green-200"
          data-testid="flyer-whatsapp-send-btn">
          {sharing ? <Loader2 className="w-6 h-6 mr-2 animate-spin" /> : <MessageCircle className="w-6 h-6 mr-2" />}
          {sharing ? 'Hazirlaniyor...' : 'WhatsApp\'a Gonder'}
        </Button>
        <p className="text-xs text-green-700/60 mt-2 text-center">Flyer otomatik indirilir ve WhatsApp acilir. Gorseli eklemeyi unutmayin!</p>
      </div>

      {/* Other Share Options */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="flyer-share-section">
        <div className="flex items-center gap-2 mb-4">
          <Share2 className="w-5 h-5 text-amber-700" />
          <h3 className="font-semibold text-charcoal">Diger Paylasim Secenekleri</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Button onClick={handleWhatsAppShare} variant="outline"
            className="flex items-center gap-2 border-green-200 hover:bg-green-50 py-3"
            data-testid="flyer-share-whatsapp">
            <MessageCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700">WhatsApp</span>
          </Button>
          <Button onClick={handleTelegramShare} variant="outline"
            className="flex items-center gap-2 border-blue-200 hover:bg-blue-50 py-3"
            data-testid="flyer-share-telegram">
            <Send className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-blue-600">Telegram</span>
          </Button>
          <Button onClick={handleFacebookShare} variant="outline"
            className="flex items-center gap-2 border-blue-300 hover:bg-blue-50 py-3"
            data-testid="flyer-share-facebook">
            <Facebook className="w-4 h-4 text-blue-700" />
            <span className="text-sm text-blue-700">Facebook</span>
          </Button>
          <Button onClick={handleCopyLink} variant="outline"
            className="flex items-center gap-2 border-stone-300 hover:bg-stone-50 py-3"
            data-testid="flyer-copy-link">
            {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4 text-stone-500" />}
            <span className="text-sm">{linkCopied ? 'Kopyalandi!' : 'Metni Kopyala'}</span>
          </Button>
        </div>
      </div>

      <div className="text-center text-sm text-charcoal/40">
        Flyer boyutu: 800x1120px | Sosyal medya ve baski icin ideal
      </div>
    </div>
  );
};

export default FlyerGenerator;
